/**
 * CSV field rendering for client-side exports.
 *
 * Quoting alone is not enough. A spreadsheet evaluates a cell that begins with `=`, `+`, `-` or `@`
 * as a formula, and quotes do not stop it, so an exported name is executable content in the reader's
 * client. The backend has the same rule in `backend/src/utils/csv.ts`; both exist because a download
 * built in the browser never passes through the server.
 */

/** Leading characters a spreadsheet treats as the start of an expression. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/** A value that only looks dangerous because it is negative. Prefixing it would corrupt the data. */
const PLAIN_NUMBER = /^-?(\d+\.?\d*|\.\d+)$/;

/**
 * One CSV field: always quoted, internal quotes doubled, formula leads neutralised.
 *
 * Always quoted, so a value containing a comma, a quote or a newline cannot shift the columns
 * after it.
 */
export function csvField(value: unknown): string {
  const raw = value === null || value === undefined ? '' : String(value);
  const body = FORMULA_LEAD.test(raw) && !PLAIN_NUMBER.test(raw) ? `'${raw}` : raw;
  return `"${body.replace(/"/g, '""')}"`;
}

/** One CSV record. */
export function csvRow(fields: readonly unknown[]): string {
  return fields.map(csvField).join(',');
}
